/** Connected civic wings. The same room boundaries author apertures, collision,
 * occupied indoor volumes and the room/portal graph. Local metres, +Z entrance. */
import * as pmath from '../pmath.js';
import {rngFromString} from '../rng.js';
import {buildFacade,type FacadePlan,type FacadeOpening} from './facade.js';
import type {BuildingPortal} from './program.js';
import type {AssetNode,AssetVec3} from '../proceduralAssets.js';
import type {PlaceBox} from './frontierTown.js';

export interface CivicRoom {
 id:string; name:string; center:AssetVec3; size:AssetVec3;
 floor:number; ceiling:number; destination:AssetVec3;
}
export interface CivicInterior {rooms:CivicRoom[];portals:BuildingPortal[];lamps:AssetVec3[]}
interface HallEmitter {
 nodes:AssetNode[];obstacles:PlaceBox[];floors:PlaceBox[];
 node(id:string,kind:AssetNode['kind'],params:number[],p:AssetVec3,mat:number,r?:AssetVec3):AssetNode;
 box(id:string,size:AssetVec3,p:AssetVec3,mat:number,solid?:boolean,floor?:boolean,r?:AssetVec3):AssetNode;
 array(n:AssetNode,count:number,offset:AssetVec3):void;
 roof(id:string,w:number,d:number,y:number,x?:number,z?:number,pitch?:number):void;
 banner(id:string,x:number,top:number,z:number,w:number,h:number):void;
 stairs(id:string,width:number,height:number,front:number,run?:number):number;
}
interface HallRule {width:number;depth:number;height:number;platform:number;tower:number}
interface HallDesign {width:number;depth:number;wingSide:number;wingDepth:number;pitch:number;tower:number}
export function buildCivicHall(b:HallEmitter,seed:string,tier:number,r:HallRule,design:HallDesign) {
 const {box,node,array,roof,banner,stairs}=b,startNode=b.nodes.length,startBox=b.obstacles.length,startFloor=b.floors.length;
 const detail=rngFromString(`${seed}:civic:hall-detail:v1`),base=r.platform;
 const w=r.width*design.width,d=r.depth*design.depth*.88,hw=w*.36,lw=w*.28,rw=w*.26;
 const back=-d/2-1.8,front=d/2-1.8,mapDepth=tier===5?4.2:0;
 const rooms:CivicRoom[]=[],portals:BuildingPortal[]=[],lamps:AssetVec3[]=[];
 const room=(id:string,name:string,x:number,z:number,width:number,depth:number,height:number)=>{
  const room:CivicRoom={id,name,center:[x,base+height/2,z],size:[width,height,depth],floor:base,ceiling:base+height,destination:[x,base,z+depth/2-1.1]};
  rooms.push(room);box(`${id}:foundation`,[width+.24,base,depth+.24],[x,base/2,z],4,false,true);return room;
 };
 const hall=room('great-hall','Great hall',0,(back+mapDepth+front)/2,hw,d-mapDepth,r.height);
 const leftBack=back+1.2,leftFront=front-2.5-(1-design.wingDepth)*2,leftD=leftFront-leftBack,split=leftBack+leftD*.49;
 room('archives','Archives',-(hw+lw)/2,(leftBack+split)/2,lw,split-leftBack,4.1);
 room('petitions','Petitions office',-(hw+lw)/2,(split+leftFront)/2,lw,leftFront-split,4.7);
 const rightBack=back+2.2,rightFront=front-(tier===5?2:5),rightD=rightFront-rightBack;
 const councilFront=tier===5?rightBack+rightD*.58:rightFront;
 room('council','Council chamber',(hw+rw)/2,(rightBack+councilFront)/2,rw,councilFront-rightBack,tier===5?6.2:5.5);
 if(tier===5){
  room('guard-room','Guard room',(hw+rw)/2,(councilFront+rightFront)/2,rw,rightFront-councilFront,4.2);
  room('map-room','Map room',0,back+mapDepth/2,hw,mapDepth,4.5);
 }
 const extents=(room:CivicRoom)=>({x0:room.center[0]-room.size[0]/2,x1:room.center[0]+room.size[0]/2,z0:room.center[2]-room.size[2]/2,z1:room.center[2]+room.size[2]/2});
 // Shared wall intervals are emitted once, at the taller room's height. The
 // neighbour overlap decides the portal, so a wing can never be a sealed box.
 for(const [ri,room] of rooms.entries()){
  const e=extents(room);
  for(const axis of ['x','z'] as const)for(const side of [-1,1] as const){
   const line=axis==='x'?(side===1?e.z1:e.z0):(side===1?e.x1:e.x0),lo=axis==='x'?e.x0:e.z0,hi=axis==='x'?e.x1:e.z1;
   const joins=rooms.flatMap((other,index)=>{
    if(index===ri)return [];const o=extents(other),opposite=axis==='x'?(side===1?o.z0:o.z1):(side===1?o.x0:o.x1);
    const a=Math.max(lo,axis==='x'?o.x0:o.z0),c=Math.min(hi,axis==='x'?o.x1:o.z1);
    return Math.abs(line-opposite)<1e-7&&c-a>.01?[{a,c,other,index}]:[];
   });
   const cuts=[...new Set([lo,hi,...joins.flatMap(j=>[j.a,j.c])])].sort((a,c)=>a-c);
   for(let k=1;k<cuts.length;k++){
    const a=cuts[k-1],c=cuts[k],mid=(a+c)/2,width=c-a,join=joins.find(j=>mid>j.a-1e-7&&mid<j.c+1e-7);
    if(width<.01||join&&join.index<ri)continue;
    const height=join?Math.max(room.size[1],join.other.size[1]):room.size[1];
    const entry=room===hall&&axis==='x'&&side===1&&!join;
    const openings:FacadeOpening[]=[];
    if(join||entry){
     const dw=entry?2.8:1.8,dh=entry?3.5:2.8;
     openings.push({id:'door',u:0,bottom:base,width:dw,height:dh,family:'door'});
     const position:AssetVec3=axis==='x'?[mid,base,line]:[line,base,mid];
     portals.push({id:join?`${room.id}:${join.other.id}`:'public-entrance',from:join?room.id:'outside',to:join?join.other.id:room.id,position,normal:axis==='x'?[0,0,side]:[side,0,0],width:dw,height:dh,capability:'walk'});
     if(entry)for(const side of [-1,1])openings.push({id:`entry-light-${side}`,u:side*(width/2-1.25),bottom:base+1.35,width:1.1,height:3.1,family:'casement'});
    }else if(width>2.1){
     const count=Math.max(1,Math.floor(width/3.3));
     for(let i=0;i<count;i++)openings.push({id:`light-${i}`,u:-width/2+width*(i+.5)/count,bottom:base+1.25,width:Math.min(1.3,width/count-.85),height:room===hall?3.25:1.9,family:'casement'});
    }
    const lowerHeight=join?Math.min(room.size[1],join.other.size[1]):height;
    const f:FacadePlan={id:`${room.id}:${axis}:${side}:${k}`,center:axis==='x'?[mid,base,line]:[line,base,mid],axis,outward:side,width,height:lowerHeight,thickness:.3,openings};
    let part='';const facadeBuilder={part(id:string,build:()=>void){const prev=part;part=id;build();part=prev;},box(size:AssetVec3,p:AssetVec3,mat:number,solid?:boolean,rotation?:AssetVec3){box(part,size,p,mat,solid,false,rotation);}};
    buildFacade(facadeBuilder,f);
    if(join&&height>lowerHeight+.01){
     const lower=room.size[1]<join.other.size[1]?room:join.other;
     const bottom=base+lowerHeight+lower.size[0]/2*pmath.sin(.45)/pmath.cos(.45)+.3,lightHeight=Math.min(1.35,base+height-bottom-.35),windows:FacadeOpening[]=[];
     if(lightHeight>.6&&width>2){const count=Math.max(1,Math.floor(width/3));for(let i=0;i<count;i++)windows.push({id:`clerestory-${i}`,u:-width/2+width*(i+.5)/count,bottom,width:1,height:lightHeight,family:'transom'});}
     buildFacade(facadeBuilder,{...f,id:`${f.id}:upper`,center:[f.center[0],base+lowerHeight,f.center[2]],height:height-lowerHeight,outward:lower===room?(side===1?-1:1):side,openings:windows});
    }
    if(!join){
     // Stone buttresses divide long elevations into bays, with angled shoulders.
     for(const u of [-width/2+.18,width/2-.18]){
      const p:AssetVec3=axis==='x'?[mid+u,base+1.15,line+side*.16]:[line+side*.16,base+1.15,mid+u];
      box('buttress-foot',axis==='x'?[.45,2.3,.58]:[.58,2.3,.45],p,4,true);
      const cap=box('buttress-shoulder',axis==='x'?[.45,.5,.58]:[.58,.5,.45],[p[0],base+2.55,p[2]],4);
      cap.modifiers=[{type:'taper',axis:'y',factor:.55}];
     }
     for(const o of openings)if(o.family!=='door'){
      // Tall inset windows receive a pointed masonry hood above the opening.
      for(const slope of [-1,1]){
       const u=mid+o.u+slope*o.width*.25,y=o.bottom+o.height+.22;
       box('window-hood',axis==='x'?[o.width*.64,.12,.42]:[.42,.12,o.width*.64],axis==='x'?[u,y,line+side*.09]:[line+side*.09,y,u],4,false,false,axis==='x'?[0,0,-slope*.38]:[slope*.38,0,0]);
      }
     }
    }
   }
  }
  const roofBounds={x0:e.x0-.4,x1:e.x1+.4,z0:e.z0-.4,z1:e.z1+.4};
  for(const other of rooms){
   if(other===room||other.ceiling<room.ceiling)continue;const o=extents(other);
   if(Math.min(e.z1,o.z1)>Math.max(e.z0,o.z0)){
    if(Math.abs(e.x1-o.x0)<1e-7)roofBounds.x1=e.x1+.08;
    if(Math.abs(e.x0-o.x1)<1e-7)roofBounds.x0=e.x0-.08;
   }
   if(Math.min(e.x1,o.x1)>Math.max(e.x0,o.x0)){
    if(Math.abs(e.z1-o.z0)<1e-7)roofBounds.z1=e.z1+.08;
    if(Math.abs(e.z0-o.z1)<1e-7)roofBounds.z0=e.z0-.08;
   }
  }
  roof(`${room.id}:roof`,roofBounds.x1-roofBounds.x0,roofBounds.z1-roofBounds.z0,room.ceiling,(roofBounds.x0+roofBounds.x1)/2,(roofBounds.z0+roofBounds.z1)/2,room===hall?design.pitch:.39+(room.id==='council'?.06:0));
  // A tiled floor follows each occupied wing, leaving the re-entrant courts open.
  const nx=Math.ceil((room.size[0]-.36)/.8),nz=Math.ceil((room.size[2]-.36)/.8),sx=(room.size[0]-.36)/nx,sz=(room.size[2]-.36)/nz;
  for(let row=0;row<nz;row++)array(box('floor-tile',[sx-.016,.014,sz-.016],[e.x0+.18+sx/2,base+.008,e.z0+.18+sz*(row+.5)],row%3?4:0),nx,[sx,0,0]);
  const lamp:AssetVec3=[room.center[0]+room.size[0]/2-.42,base+2.6,room.center[2]];lamps.push(lamp);
  box('lamp-bracket',[.36,.1,.13],[lamp[0]+.1,lamp[1]+.26,lamp[2]],1);
  node('lamp-glow','cylinder',[.1,.12,.32],lamp,11);
  for(const dy of [-.2,.2])box('lamp-cap',[.27,.06,.27],[lamp[0],lamp[1]+dy,lamp[2]],7);
 }
 // The great hall has exposed trusses and clear central processional space.
 const hz=hall.center[2],hd=hall.size[2],trusses=Math.max(3,Math.floor(hd/3));
 for(let i=0;i<trusses;i++){
  const z=hz-hd/2+1.2+(hd-2.4)*i/(trusses-1),peak=hall.ceiling+hw/2*pmath.sin(design.pitch)/pmath.cos(design.pitch);
  box('hall-tie',[hw-.2,.24,.24],[0,hall.ceiling-.2,z],1);
  box('king-post',[.18,peak-hall.ceiling,.18],[0,(peak+hall.ceiling)/2,z],1);
  for(const side of [-1,1])box('truss-rafter',[hw/2/pmath.cos(design.pitch),.18,.2],[side*hw/4,hall.ceiling+(peak-hall.ceiling)/2-.1,z],1,false,false,[0,0,-side*design.pitch]);
 }
 const table=(id:string,x:number,z:number,width:number,depth:number)=>{
  box(`${id}:top`,[width,.14,depth],[x,base+.85,z],1,true);
  for(const sx of [-1,1])for(const sz of [-1,1])box(`${id}:leg`,[.14,.78,.14],[x+sx*(width/2-.15),base+.39,z+sz*(depth/2-.15)],1,true);
 };
 for(const side of [-1,1]){
  const x=side*(hw/2-1.25);
  let bands=[[hz-hd/2+1,hz+hd/2-1]];
  for(const p of portals.filter(p=>Math.abs(p.position[0]-side*hw/2)<.01)){
   const lo=p.position[2]-p.width/2-.6,hi=p.position[2]+p.width/2+.6;
   bands=bands.flatMap(([a,c])=>hi<=a||lo>=c?[[a,c]]:[[a,Math.max(a,lo)],[Math.min(c,hi),c]]).filter(([a,c])=>c-a>1.7);
  }
  for(const [a,c] of bands){
   const length=Math.min(5,c-a),z=(a+c)/2;
   table('hall-table',x,z,.82,length);
   box('hall-bench',[.38,.48,length],[x-side*.85,base+.24,z],1,true);
   for(let i=0;i<3;i++)node('hall-cup','cylinder',[.085,.075,.14],[x,base+1,z-length*.3+i*length*.3],7);
   box('hall-book',[.24,.06,.32],[x+side*.16,base+.96,z+length*.28],6);
  }
 }
 box('hall-runner',[2.05,.017,hd-1.1],[0,base+.023,hz],5);
 for(const x of [-1.06,1.06])box('runner-border',[.07,.018,hd-1.1],[x,base+.025,hz],7);
 for(const side of [-1,1])banner('hall-tapestry',side*hw*.3,base+5.9,hz-hd/2+.2,1,2.5);
 for(const room of rooms.filter(room=>room!==hall)){
  const e=extents(room),x=room.center[0],z=room.center[2];
  if(room.id==='archives'){
   const xx=e.x0+.65;
   box('archive-case',[.65,2.45,room.size[2]-3.4],[xx,base+1.225,z+.75],1,true);
   for(let level=0;level<4;level++)for(let j=0;j<4;j++)box('archive-ledger',[.2,.3,.34],[xx+.35,base+.38+level*.53,e.z0+2.55+j*(room.size[2]-3.8)/4],detail()<.5?5:6);
   box('archive-rear-case',[room.size[0]-3.2,2.2,.65],[x+1,base+1.1,e.z0+.6],1,true);
  }else if(room.id==='council'){
   const radius=Math.min(1.45,room.size[0]*.23);
   node('council-table','cylinder',[radius,radius,.16],[x,base+.84,z-.3],1);
   b.obstacles.push({center:[x,base+.84,z-.3],size:[radius*2,.16,radius*2]});
   box('council-pedestal',[.5,.75,.5],[x,base+.375,z-.3],4,true);
   for(const side of [-1,1])for(const end of [-1,1]){
    const cx=x+side*.8,cz=z-.3+end*(radius+.65);
    box('council-chair',[.48,.5,.5],[cx,base+.25,cz],1,true);box('chair-back',[.48,.65,.12],[cx,base+.75,cz+end*.2],5,true);
   }
   box('council-map',[1.25,.025,.9],[x,base+.94,z-.3],6);
  }else if(room.id==='guard-room'){
   const span=(room.size[0]-3.2)/2;
   for(const side of [-1,1])box('guard-bench',[span,.5,.65],[x+side*(1.25+span/2),base+.25,e.z0+.6],1,true);
   for(let i=0;i<4;i++){box('spear',[.055,2.3,.055],[e.x1-.5,base+1.2,z-1+i*.55],1);node('spear-point','cone',[.12,.3],[e.x1-.5,base+2.45,z-1+i*.55],7);}
   box('weapon-rack',[.55,2.4,2.4],[e.x1-.48,base+1.2,z],1,true);
  }else{
   table('office-desk',x,z-.5,Math.min(2.6,room.size[0]-2.4),.9);
   box('office-records',[.6,.07,.42],[x-.5,base+.97,z-.5],6);
   if(room.id==='map-room')box('survey-map',[1.4,.025,.7],[x,base+.945,z-.5],5);
   box('office-chair',[.5,.48,.5],[x,base+.24,z-1.4],1,true);
  }
 }
 // A real covered forecourt recesses the hall door behind the public stair.
 const porchW=tier===5?5.5:4.9,porchD=2;
 box('entrance-landing',[porchW+.5,base,porchD+.2],[0,base/2,front+porchD/2],4,false,true);
 for(const side of [-1,1]){
  box('porch-column',[.38,3.9,.38],[side*(porchW/2-.2),base+1.95,front+1.7],4,true);
  box('porch-capital',[.62,.24,.62],[side*(porchW/2-.2),base+3.65,front+1.7],4);
 }
 roof('entrance-canopy',porchW+.5,2.4,base+3.9,0,front+.9,.36);
 banner('hall-standard',0,hall.ceiling-.35,front+.24,tier===5?1.45:1.15,2.7);
 const toe=stairs('hall-public-stair',porchW-.35,base,front+porchD+.1);
 // An octagonal archive tower and smaller pavilion break the roof silhouette.
 // They occupy reserved corners, leaving circulation through every wing clear.
 const tower=(x:number,z:number,height:number,radius:number)=>{
  const n=node('civic-tower','lathe',[8],[x,base,z],4,[0,Math.PI/8,0]);n.profile=[[radius*1.12,0],[radius*1.12,.6],[radius,.8],[radius,height-.55],[radius*1.15,height-.55],[radius*1.15,height]];
  for(const size of [[radius*1.9,height,radius*1.05],[radius*1.05,height,radius*1.9]])b.obstacles.push({center:[x,base+height/2,z],size:size as AssetVec3});
  const cap=node('tower-roof','lathe',[8],[x,base+height,z],2,[0,Math.PI/8,0]);cap.profile=[[radius*1.3,0],[radius*1.3,.15],[0,1.5]];
  for(const y of [height*.4,height*.7,height-.95])box('tower-slit',[.25,.85,.1],[x,base+y,z+radius+.02],5);
  banner('tower-colors',x,base+height-2,z+radius+.08,.72,2.3);
 };
 tower(-hw/2-lw+.6,leftBack+.5,r.tower*design.tower,tier===5?1.85:1.5);
 if(tier===5)tower(hw/2+rw-.5,rightFront-.45,r.height*.91,1.05);
 // Roof lantern gives the long hall a second, lighter vertical accent.
 const lanternY=hall.ceiling+hw/2*pmath.sin(design.pitch)/pmath.cos(design.pitch),lanternZ=hz-hd*.2;
 box('lantern-base',[2.25,.5,2.1],[0,lanternY+.05,lanternZ],4);
 for(const x of [-.9,.9])for(const z of [-.8,.8])box('lantern-pier',[.2,1.4,.2],[x,lanternY+.9,lanternZ+z],4);
 roof('lantern-roof',2.7,2.5,lanternY+1.65,0,lanternZ,.42);
 const interior:CivicInterior={rooms,portals,lamps};
 // Mirror the complete structural result, including instance-array offsets,
 // openings and semantic data; decoration cannot reroll the floor plan.
 if(design.wingSide<0){
  for(const n of b.nodes.slice(startNode)){n.p[0]*=-1;n.r[1]*=-1;n.r[2]*=-1;for(const mod of n.modifiers??[])if(mod.type==='array'&&mod.mode==='linear')mod.offset[0]*=-1;}
  for(const box of [...b.obstacles.slice(startBox),...b.floors.slice(startFloor)])box.center[0]*=-1;
  for(const room of rooms){room.center[0]*=-1;room.destination[0]*=-1;}
  for(const p of portals){p.position[0]*=-1;p.normal[0]*=-1;}
  // Node positions are the same arrays as lamp anchors and were mirrored above.
 }
 return {entrance:[0,0,toe+.06] as AssetVec3,entryApproach:[0,0,r.depth/2+3] as AssetVec3,interior};
}
